import { Button, Heading, Text, VStack } from "@chakra-ui/react"
import classnames from "classnames"
import React from "react"
import withTranslate from "../lng/withTranslate"
import Pathicon from "../pathicon/Pathicon"

// Key used for matching in regex pattern and for storing in sessionStorage.
const chunkUrlKey = "chunkUrl"

// client deploys can lead to the loading chunk failures
// if user has stale manifest and triggers a lazy-loaded
// chunk to be fetched
class LoadingChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    if (error.name === "ChunkLoadError") {
      // Note: the re match may include any trailing characacters (like a closing parenthesis),
      // so be mindful in case you wish to use the value as a url. Currently only used for
      // comparison.
      const matchedChunkUrl =
        error.request || error.message.match(/https?:\/\/\S+/)?.[0]
      // If we haven't handled this chunk yet, we'll store the chunk name in sessionStorage
      // and reload the page, hoping that the chunk will load (likely will, as the chunk is
      // likely included in a stale manifest, and the user will get the latest manifest after
      // reloading).
      const storedChunkUrl = sessionStorage.getItem(chunkUrlKey)

      // When the stored chunk name is different from the matched chunk name,
      // it means we haven't handled this chunk yet.
      const chunkUrlValue =
        storedChunkUrl !== matchedChunkUrl ? matchedChunkUrl : null

      return { error, [chunkUrlKey]: chunkUrlValue }
    }

    // When not a loading chunk error, propagate the error to a parent
    // ErrorBoundary component as otherwise React will attempt to rerender this tree
    // and the error will be thrown again and again (at least in local dev).
    throw error
  }

  // Always called after getDerivedStateFromError, but only when getDerivedStateFromError
  // returns the new state, not when it throws, which means it will run when a loading
  // chunk error occurs which we haven't already attempted to handle.
  componentDidCatch(error) {
    if (this.state[chunkUrlKey]) {
      sessionStorage.setItem(chunkUrlKey, this.state[chunkUrlKey])
      window.location.reload()
    }
  }

  render() {
    // When a chunk error has occurred and we haven't yet attempted a page reload,
    // we'll simply render nothing, which should reveal the initial page load UI
    // while the page reloads.
    if (this.state[chunkUrlKey]) {
      return null
    }

    if (this.state.error) {
      const { t } = this.props

      return (
        <VStack
          className={classnames(
            "LoadingChunkErrorBoundary",
            this.props.className
          )}
          w="100%"
          minH="100%"
          maxH="100vh"
          justifyContent="center"
          alignItems="center"
          bg="white"
          p={2}
          spacing={4}
        >
          <Pathicon icon="rocket" style={{ fontSize: "2em" }} />
          <Heading as="h1" size="h4" m={0}>
            {t("failure_to_retrieve_updates.heading.Updates unavailable")}
          </Heading>
          <Text m={0} maxW="400px" textAlign="center">
            {t(
              "failure_to_retrieve_updates.body.A newer version has been released but there was an issue retrieving the update."
            )}
          </Text>
          <Button
            colorScheme="brand"
            variant="solid"
            cursor="pointer"
            link={`mailto:${window.school?.email || ""}`}
          >
            {t("failure_to_retrieve_updates.action.Contact support")}
          </Button>
        </VStack>
      )
    }

    return this.props.children
  }
}

LoadingChunkErrorBoundary.displayName = "LoadingChunkErrorBoundary"

export default withTranslate(LoadingChunkErrorBoundary)
